import { useEffect, useRef } from "react";

import { Box } from "@mui/material";

import { useSubmitQuoteNoCapacityCommandLink } from "./useSubmitQuoteNoCapacityCommandLink";
import { NoCapacityButton } from "../../../Carrier/components/NoCapacityButton/NoCapacityButton";

interface UnauthenticatedNoCapacityButtonContainerProps {
  token: string;
  shipmentId: number;
  shipperCompanyId: number;
  initialHasMarkedNoCapacity: boolean;
  // The carrier came from the email's "No Truck Available" button.
  arrivedFromNoCapacityEmail: boolean;
  onRecoverableFailure: (outcomeCode: string) => void;
  onBlockedFailure: (blockedReasonCode: string) => void;
}

/**
 * The landing's counterpart of the in-app NoCapacityButtonContainer: the same
 * presentational control, marked and undone through the signed token instead of
 * the authenticated endpoints. Rendered as the quote form's secondary action, so it
 * shows exactly where the form does
 * (docs/engineering/design/carrier_submit_quote_command_link_no_capacity_design.md D4, §8).
 */
export function UnauthenticatedNoCapacityButtonContainer(
  props: UnauthenticatedNoCapacityButtonContainerProps,
) {
  const {
    token,
    shipmentId,
    shipperCompanyId,
    initialHasMarkedNoCapacity,
    arrivedFromNoCapacityEmail,
    onRecoverableFailure,
    onBlockedFailure,
  } = props;
  const containerRef = useRef<HTMLDivElement>(null);
  const {
    hasMarkedNoCapacity,
    markNoCapacity,
    undoNoCapacity,
    loading,
    error,
  } = useSubmitQuoteNoCapacityCommandLink({
    token,
    shipmentId,
    shipperCompanyId,
    initialHasMarkedNoCapacity,
    onRecoverableFailure,
    onBlockedFailure,
  });

  /**
   * The control sits under the shipment card and the quote fields. A carrier who
   * clicked "No Truck Available" in the email is brought straight to it, so the
   * confirming click is the first thing they see.
   */
  useEffect(() => {
    if (arrivedFromNoCapacityEmail && !initialHasMarkedNoCapacity) {
      containerRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [arrivedFromNoCapacityEmail, initialHasMarkedNoCapacity]);

  return (
    <Box ref={containerRef}>
      <NoCapacityButton
        hasMarkedNoCapacity={hasMarkedNoCapacity}
        isLoading={loading}
        error={error}
        onMark={markNoCapacity}
        onUndo={undoNoCapacity}
        markLabel={
          arrivedFromNoCapacityEmail ? "Confirm no truck available" : undefined
        }
      />
    </Box>
  );
}
